import {
  getFirestore,
  collection,
  addDoc,
  getDocs,
  getDoc,
  doc,
  deleteDoc,
  updateDoc,
  query,
  where,
  orderBy,
  Timestamp,
} from "firebase/firestore";

import {
  getStorage,
  ref,
  uploadBytes,
  getDownloadURL,
  deleteObject,
} from "firebase/storage";
import { getAuth } from "firebase/auth";

const db = getFirestore();
const auth = getAuth();
export const storage = getStorage(
  undefined,
  "gs://trade-a1284.firebasestorage.app"
);

export const deleteTrade = async (tradeId: string) => {
  const userId = auth.currentUser?.uid;

  if (!userId) {
    throw new Error("Utilisateur non connecté. Veuillez vous reconnecter.");
  }

  if (!tradeId) {
    throw new Error("ID du trade invalide.");
  }

  const tradeRef = doc(db, `users/${userId}/trades/${tradeId}`);
  const tradeSnap = await getDoc(tradeRef);

  if (!tradeSnap.exists()) {
    throw new Error("Trade non trouvé.");
  }

  const tradeData = tradeSnap.data();
  const imageUrls = tradeData?.imageUrls || [];

  // Suppression des images associées dans Firebase Storage
  for (const imageUrl of imageUrls) {
    try {
      const imageRef = ref(storage, imageUrl);
      await deleteObject(imageRef);
      console.log(`Image supprimée: ${imageUrl}`);
    } catch (error) {
      console.error(
        `Erreur lors de la suppression de l'image: ${imageUrl}`,
        error
      );
    }
  }

  // Suppression du document dans Firestore
  await deleteDoc(tradeRef);
  console.log("Trade supprimé !");
};

/**
 * Ajoute un trade pour un utilisateur à une date spécifique.
 */
export const addTrade = async (
  date: string,
  tradeData: { note: string; status: "Win" | "Loss"; imageUrls: string[] }
) => {
  const userId = auth.currentUser?.uid;
  if (!userId) throw new Error("Utilisateur non authentifié");

  const tradeRef = collection(db, `users/${userId}/trades`);
  const newTrade = await addDoc(tradeRef, {
    ...tradeData,
    date, // Stocke la date du trade
    createdAt: Timestamp.now(),
  });

  return { id: newTrade.id, ...tradeData }; // ✅ Retourne l'ID pour la redirection
};

/**
 * Récupère un trade spécifique par son ID.
 */
export const getTrade = async (userId: string, tradeId: string) => {
  const tradeDoc = await getDoc(doc(db, `users/${userId}/trades`, tradeId));

  if (!tradeDoc.exists()) return null;

  const tradeData = tradeDoc.data();
  return {
    id: tradeDoc.id,
    note: tradeData?.note || "", // ✅ Vérification pour éviter `undefined`
    status: tradeData?.status || "Win",
    imageUrls: tradeData?.imageUrls || [],
  };
};

/**
 * Met à jour un trade existant (note, statut, images).
 */
export const updateTrade = async (
  userId: string,
  tradeId: string,
  tradeData: any
) => {
  return updateDoc(doc(db, `users/${userId}/trades`, tradeId), tradeData);
};

/**
 * Upload plusieurs images de trade et retourne leurs URLs.
 */
export const uploadTradeImages = async (
  files: FileList | File[],
  userId: string
) => {
  if (!files || files.length === 0)
    throw new Error("Aucun fichier sélectionné.");

  const urls: string[] = [];

  for (let i = 0; i < files.length; i++) {
    const fileRef = ref(
      storage,
      `users/${userId}/trades/${Date.now()}_${files[i].name}`
    );
    await uploadBytes(fileRef, files[i]);
    const url = await getDownloadURL(fileRef);
    urls.push(url);
  }

  return urls; // ✅ Retourne un tableau d'URLs
};

/**
 * Upload une seule image et retourne son URL.
 */
export const uploadTradeImage = async (file: File, userId: string) => {
  if (!file) throw new Error("Aucun fichier sélectionné.");

  const fileRef = ref(
    storage,
    `users/${userId}/trades/${Date.now()}_${file.name}`
  );
  await uploadBytes(fileRef, file);
  return await getDownloadURL(fileRef);
};

/**
 * Récupère tous les trades pour un utilisateur à une date donnée.
 */
export const getTrades = async (date: string) => {
  const userId = auth.currentUser?.uid;
  if (!userId) throw new Error("Utilisateur non authentifié");

  const tradeRef = collection(db, `users/${userId}/trades`);
  const q = query(
    tradeRef,
    where("date", "==", date),
    orderBy("createdAt", "desc")
  );

  const querySnapshot = await getDocs(q);

  return querySnapshot.docs.map((doc) => ({
    id: doc.id,
    ...doc.data(),
  }));
};
